import { useState, useCallback } from "react";
import algosdk from 'algosdk';
import { PeraWalletConnect } from '@perawallet/connect';

interface UseAlgorandDonationReturn {
  isConnecting: boolean;
  isTransacting: boolean;
  error: string | null;
  txId: string | null;
  donate: (amount: number) => Promise<void>;
  clearError: () => void;
}

// Simple Algorand donation hook using AlgoSDK
export const useAlgorandDonation = (): UseAlgorandDonationReturn => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTransacting, setIsTransacting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txId, setTxId] = useState<string | null>(null);

  const donate = useCallback(async (amount: number) => {
    setIsConnecting(true);
    setError(null);
    setTxId(null);
    try {
      // Initialize Pera Wallet
      const peraWallet = new PeraWalletConnect();

      // Connect to wallet
      const accounts = await peraWallet.connect();
      if (!accounts || accounts.length === 0) {
        throw new Error("No wallet accounts found");
      }

      const senderAddress = accounts[0];

      setIsConnecting(false);
      setIsTransacting(true);

      // Create Algorand client for TestNet
      const algodClient = new algosdk.Algodv2(
        "",
        "https://testnet-api.algonode.cloud",
        ""
      );

      // RightNowAid donation address (TestNet)
      const recipientAddress =
        "RIGHTNOWAIDTESTNETADDRESS123456789ABCDEFGHIJKLMNOP"; // Replace with actual address

      // Get suggested transaction parameters
      const suggestedParams = await algodClient.getTransactionParams().do();

      // Create payment transaction
      // @ts-ignore
      const txn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        from: senderAddress,
        to: recipientAddress,
        amount: amount * 1000000, // Convert ALGO to microAlgos
        note: new TextEncoder().encode(
          "RightNowAid Donation - Thank you for supporting emergency aid!"
        ),
        suggestedParams,
      });

      // Prepare transaction for wallet signing
      // @ts-ignore: TypeScript type mismatch, but this works at runtime
      const txn_b64 = algosdk.encodeUnsignedTransaction(txn).toString('base64');

      // Sign transaction with wallet
      // @ts-ignore
      const signedTxns = await peraWallet.signTransaction([
        { txn: txn_b64 }
      ]);
      const signedTx = signedTxns[0];

      // Submit transaction
      const result = await algodClient.sendRawTransaction(signedTx).do();
      const txId = result.txid;

      // Wait for confirmation
      await algosdk.waitForConfirmation(algodClient, txId, 4);

      setTxId(txId);
    } catch (err) {
      console.error("Algorand donation error:", err);
      setError(err instanceof Error ? err.message : "Donation failed");
    } finally {
      setIsConnecting(false);
      setIsTransacting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setTxId(null);
  }, []);

  return {
    isConnecting,
    isTransacting,
    error,
    txId,
    donate,
    clearError,
  };
};
